import { getRecordsWithOperationsApplied, op, Operation } from "libs/transaction";
import * as ops from "libs/operations";
import { createAction } from "./action";
import { InvalidActionStateError } from "./utils/errors";
import { UnreachableCaseError } from "libs/errors";
import {
  DraftAttachmentDoc,
  DraftLabelDoc,
  getMapRecords,
  getPointer,
  ThreadVisibility,
  generateRecordId,
  DraftUserRecipientDoc,
  DraftGroupRecipientDoc,
  DraftRecipientDoc,
} from "libs/schema";
import { uniqBy, pickBy } from "lodash-comms";
import { undeleteOperation } from "./utils/operations";
import { isDefined } from "libs/predicates";
import { Merge } from "type-fest";
import { authorizeUserCanCreateDraft, authorizeUserCanUpdateDraft } from "./authorizers/draftAuthorizers";
import { authorizeUserCanSendMessage } from "./authorizers/messageAuthorizers";

export interface EditorMention {
  type: "user" | "group";
  id: string;
  priority: number;
  /**
   * Note that it's possible for both dueDate and requestComplete to be non-null
   * if the sender both marked an existing request from this recipient as complete and also
   * made a request of their own to this recipient.
   */
  dueDate?: string;
  requestComplete?: boolean;
}

export type DraftRecipient = { type: "user" | "group" | "email"; id: string };

/* -------------------------------------------------------------------------------------------------
 *  createNewThreadDraft
 * -------------------------------------------------------------------------------------------------
 */

type CreateNewThreadDraftProps = {
  type: "COMMS" | "EMAIL";
  draftId: string;
  ownerOrganizationId: string;
  threadId: string;
  branchedFrom?: null | {
    threadId: string;
    messageId: string;
  };
  subject?: string | null;
  visibility?: ThreadVisibility | null;
  bodyHTML?: string;
  to?: DraftRecipient[];
  cc?: DraftRecipient[];
  bcc?: DraftRecipient[];
  userMentions?: EditorMention[];
  groupMentions?: EditorMention[];
  attachments?: DraftAttachmentDoc[];
};

export const createNewThreadDraft = createAction<CreateNewThreadDraftProps>("draft.createNewThreadDraft", {
  operations: async (props, context) => {
    switch (props.type) {
      case "COMMS": {
        if (props.cc && props.cc.length > 0) {
          const message = "Draft CC recipients not supported yet.";

          throw new InvalidActionStateError(`[createNewThreadDraft] ${message}`, { userMessage: message });
        } else if (props.bcc && props.bcc.length > 0) {
          const message = "Draft BCC recipients not supported yet.";

          throw new InvalidActionStateError(`[createNewThreadDraft] ${message}`, { userMessage: message });
        }

        return ops.draft.createDraft({
          draftId: props.draftId,
          userId: context.currentUserId,
          type: props.type,
          isReply: false,
          ownerOrganizationId: props.ownerOrganizationId,
          threadId: props.threadId,
          attachments: props.attachments,
          bodyHTML: props.bodyHTML,
          branchedFrom: props.branchedFrom,
          subject: props.subject,
          visibility: props.visibility,
          to: getToRecipients(props),
        });
      }
      case "EMAIL": {
        throw new InvalidActionStateError("[createNewThreadDraft] Email drafts are not yet supported.");
      }
      default: {
        throw new UnreachableCaseError(props.type);
      }
    }
  },
  authorize: async (props, context) => {
    await authorizeUserCanCreateDraft({ organizationId: props.ownerOrganizationId, context });
  },
});

/* -------------------------------------------------------------------------------------------------
 *  createReplyDraft
 * -------------------------------------------------------------------------------------------------
 */

type CreateReplyDraftProps = {
  type: "COMMS" | "EMAIL";
  draftId: string;
  ownerOrganizationId: string;
  threadId: string;
  branchedFrom?: null | {
    threadId: string;
    messageId: string;
  };
  bodyHTML?: string;
  to?: DraftRecipient[];
  cc?: DraftRecipient[];
  bcc?: DraftRecipient[];
  userMentions?: EditorMention[];
  groupMentions?: EditorMention[];
  attachments?: DraftAttachmentDoc[];
};

export const createReplyDraft = createAction<CreateReplyDraftProps>("draft.createReplyDraft", {
  operations: async (props, context) => {
    switch (props.type) {
      case "COMMS": {
        if (props.cc && props.cc.length > 0) {
          const message = "Draft CC recipients not supported yet.";

          throw new InvalidActionStateError(`[createReplyDraft] ${message}`, { userMessage: message });
        } else if (props.bcc && props.bcc.length > 0) {
          const message = "Draft BCC recipients not supported yet.";

          throw new InvalidActionStateError(`[createReplyDraft] ${message}`, { userMessage: message });
        }

        return ops.draft.createDraft({
          draftId: props.draftId,
          userId: context.currentUserId,
          type: props.type,
          isReply: true,
          ownerOrganizationId: props.ownerOrganizationId,
          threadId: props.threadId,
          attachments: props.attachments,
          bodyHTML: props.bodyHTML,
          branchedFrom: props.branchedFrom,
          to: getToRecipients(props),
        });
      }
      case "EMAIL": {
        throw new InvalidActionStateError("[createReplyDraft] Email drafts are not yet supported.");
      }
      default: {
        throw new UnreachableCaseError(props.type);
      }
    }
  },
  authorize: async (props, context) => {
    await authorizeUserCanCreateDraft({
      organizationId: props.ownerOrganizationId,
      threadId: props.threadId,
      context,
    });
  },
});

/* -------------------------------------------------------------------------------------------------
 *  setDraft
 * -------------------------------------------------------------------------------------------------
 */

type SetDraftProps = {
  type: "COMMS" | "EMAIL";
  draftId: string;
  ownerOrganizationId: string;
  is_reply: boolean;
  is_edit: boolean;
  threadId: string;
  branchedFrom: null | {
    threadId: string;
    messageId: string;
  };
  subject: string | null;
  bodyHTML: string;
  to: DraftRecipient[];
  cc: DraftRecipient[];
  bcc: DraftRecipient[];
  visibility: ThreadVisibility | null;
  userMentions: EditorMention[];
  groupMentions: EditorMention[];
  labels: DraftLabelDoc[];
  attachments: DraftAttachmentDoc[];
};

/**
 * setDraft should only be used for updating existing drafts (and not creating new ones)
 */
export const setDraft = createAction<SetDraftProps>("draft.setDraft", {
  operations: async (props, context) => {
    const [draft] = await context.environment.loader.getRecord("draft", props.draftId, { includeSoftDeletes: true });

    if (draft?.deleted_at) {
      throw new InvalidActionStateError("[setDraft] Draft has already been deleted", {
        userMessage: "SUPPRESS",
        errorData: {
          props,
          draft,
        },
      });
    }

    switch (props.type) {
      case "COMMS": {
        if (props.cc && props.cc.length > 0) {
          const message = "Draft CC recipients not supported yet.";

          throw new InvalidActionStateError(`[setDraft] ${message}`, { userMessage: message });
        } else if (props.bcc && props.bcc.length > 0) {
          const message = "Draft BCC recipients not supported yet.";

          throw new InvalidActionStateError(`[setDraft] ${message}`, { userMessage: message });
        }

        return ops.draft.mergeDraft({
          draftId: props.draftId,
          userId: context.currentUserId,
          type: props.type,
          is_edit: props.is_edit,
          is_reply: props.is_reply,
          ownerOrganizationId: props.ownerOrganizationId,
          threadId: props.threadId,
          labels: props.labels,
          attachments: props.attachments,
          bodyHTML: props.bodyHTML,
          branchedFrom: props.branchedFrom,
          subject: props.subject,
          visibility: props.visibility,
          to: getToRecipients(props),
        });
      }
      case "EMAIL": {
        throw new InvalidActionStateError("[setDraft] Email drafts are not yet supported.");
      }
      default: {
        throw new UnreachableCaseError(props.type);
      }
    }
  },
  authorize: async (props, context) => {
    await authorizeUserCanUpdateDraft({ draftId: props.draftId, context });
  },
});

/* -------------------------------------------------------------------------------------------------
 *  sendDraft
 * -------------------------------------------------------------------------------------------------
 */

type SendDraftProps = {
  draftId: string;
  type: "COMMS" | "EMAIL";
  ownerOrganizationId: string;
  is_reply: boolean;
  is_edit: boolean;
  threadId: string;
  branchedFrom?: null | {
    threadId: string;
    messageId: string;
  };
  subject?: string | null;
  bodyHTML: string;
  to: DraftRecipient[];
  cc: DraftRecipient[];
  bcc: DraftRecipient[];
  visibility?: ThreadVisibility | null;
  userMentions: EditorMention[];
  groupMentions: EditorMention[];
  labels: DraftLabelDoc[];
  attachments: DraftAttachmentDoc[];
  scheduledToBeSentAt: Date | string;
  scheduledToBeSentAtTz: string | null;
};

export const sendDraft = createAction<SendDraftProps>("draft.sendDraft", {
  operations: async (props, context) => {
    const { logger, loader } = context.environment;

    const operations: Operation[] = [];

    // Check to make sure the draft exists
    const [existingDraft] = await loader.getRecord("draft", props.draftId);

    if (!existingDraft) {
      throw new InvalidActionStateError("[sendDraft] Draft not found", { errorData: { id: props.draftId } });
    }

    switch (props.type) {
      case "COMMS": {
        if (props.cc && props.cc.length > 0) {
          const message = "Draft CC recipients not supported yet.";

          throw new InvalidActionStateError(`[sendDraft] ${message}`, { userMessage: message });
        } else if (props.bcc && props.bcc.length > 0) {
          const message = "Draft BCC recipients not supported yet.";

          throw new InvalidActionStateError(`[sendDraft] ${message}`, { userMessage: message });
        }

        operations.push(
          ...ops.draft.mergeDraft({
            draftId: props.draftId,
            userId: context.currentUserId,
            type: props.type,
            is_edit: props.is_edit,
            is_reply: props.is_reply,
            ownerOrganizationId: props.ownerOrganizationId,
            threadId: props.threadId,
            labels: props.labels,
            attachments: props.attachments,
            bodyHTML: props.bodyHTML,
            branchedFrom: props.branchedFrom,
            subject: props.subject,
            visibility: props.visibility,
            to: getToRecipients(props),
          }),
        );

        break;
      }
      case "EMAIL": {
        throw new InvalidActionStateError("[sendDraft] Email drafts are not yet supported.");
      }
      default: {
        throw new UnreachableCaseError(props.type);
      }
    }

    const afterRecordMap = await getRecordsWithOperationsApplied(loader, {
      operations,
      authorId: context.currentUserId,
    });

    const [draft] = getMapRecords(afterRecordMap, "draft");

    if (!draft) {
      throw new InvalidActionStateError(`[sendDraft] Draft not found after merge`, {
        errorData: { draftId: props.draftId },
      });
    }

    if (draft.is_reply) {
      logger.notice({ draftId: draft.id }, `[sendDraft] [reply] sending draft ...`);

      const [thread] = await loader.getRecord("thread", draft.thread_id);

      if (!thread) {
        throw new InvalidActionStateError(`[sendDraft] Thread not found during reply`, {
          errorData: { draftId: draft.id, threadId: draft.thread_id },
        });
      }

      operations.push(
        ...ops.draft.sendDraftReply({
          scheduledToBeSentAt: new Date(props.scheduledToBeSentAt),
          scheduledToBeSentAtTz: props.scheduledToBeSentAtTz,
          draft,
          thread,
        }),
      );
    } else {
      logger.notice({ draftId: draft.id }, `[sendDraft] [new] sending draft ...`);

      const branchedFromMessage =
        draft.branched_from_message_id ?
          await loader.getRecord("message", draft.branched_from_message_id).then(([m]) => m)
        : undefined;

      operations.push(
        ...ops.draft.sendNewThreadDraft({
          scheduledToBeSentAt: new Date(props.scheduledToBeSentAt),
          scheduledToBeSentAtTz: props.scheduledToBeSentAtTz,
          draft,
          branchedFromMessage: branchedFromMessage || undefined,
        }),
      );
    }

    // Delete the draft now that it's been sent.
    operations.push(op.delete("draft", draft));

    // Delete the branched draft timeline entry if it exists.
    if (draft.branched_from_thread_id) {
      const pointer = getPointer("thread_timeline", {
        entry_id: props.draftId,
        thread_id: draft.branched_from_thread_id,
      });

      operations.push(op.delete(pointer));
    }

    return operations;
  },
  authorize: async (props, context) => {
    await authorizeUserCanSendMessage({
      draftId: props.draftId,
      branchedFrom: props.branchedFrom,
      context,
    });
  },
});

/* -------------------------------------------------------------------------------------------------
 *  deleteDraft
 * -------------------------------------------------------------------------------------------------
 */

type DeleteDraftProps = {
  id: string;
};

export const deleteDraft = createAction<DeleteDraftProps>("draft.deleteDraft", {
  operations: async (props, context) => {
    const [draft] = await context.environment.loader.getRecord("draft", props.id);

    if (!draft) {
      context.environment.logger.warn({ props }, "[deleteDraft.operations] Draft not found");

      return [];
    }

    const operations: Operation[] = [];

    operations.push(op.delete("draft", draft));

    if (draft.branched_from_thread_id) {
      const pointer = getPointer("thread_timeline", {
        entry_id: props.id,
        thread_id: draft.branched_from_thread_id,
      });

      operations.push(
        op.upsert(pointer, {
          onUpdate: [op.delete(pointer)],
        }),
      );
    }

    return operations;
  },
  authorize: async (props, context) => {
    await authorizeUserCanUpdateDraft({ draftId: props.id, context });
  },
});

/* -------------------------------------------------------------------------------------------------
 *  unDeleteDraft
 * -------------------------------------------------------------------------------------------------
 */

type UnDeleteDraftProps = {
  id: string;
};

export const unDeleteDraft = createAction<UnDeleteDraftProps>("draft.unDeleteDraft", {
  operations: async (props, context) => {
    const [draft] = await context.environment.loader.getRecord("draft", props.id, { includeSoftDeletes: true });

    if (!draft) {
      context.environment.logger.warn({ props }, "[unDeleteDraft.operations] Draft not found");
      return [];
    }

    if (!draft.deleted_at) {
      context.environment.logger.warn({ props }, "[unDeleteDraft.operations] Draft already undeleted");
      return [];
    }

    const operations: Operation[] = [undeleteOperation(getPointer("draft", props.id))];

    if (draft.branched_from_thread_id) {
      operations.push(
        undeleteOperation(
          getPointer("thread_timeline", {
            entry_id: props.id,
            thread_id: draft.branched_from_thread_id,
          }),
        ),
      );
    }

    return operations;
  },
  authorize: async (props, context) => {
    await authorizeUserCanUpdateDraft({ draftId: props.id, context });
  },
});

/* -------------------------------------------------------------------------------------------------
 *  getToRecipients
 * -------------------------------------------------------------------------------------------------
 */

export function getToRecipients(
  props: Partial<Pick<SetDraftProps, "to" | "userMentions" | "groupMentions">>,
): DraftRecipientDoc[] {
  const { userMentions = [], groupMentions = [], to = [] } = props;

  type RecipientOption = Merge<EditorMention, { is_mentioned: boolean; type: "user" | "group" | "email" }>;

  // Note that drafts for replies only add recipients that are *new* to the thread to the `to`
  // field. If someone is mentioned in a reply who is already participating in the thread, they
  // will not be added to the `to` field. Because of this, we need to ensure that all mentioned
  // users and groups are included as recipients when constructing the message.
  const toRecipientOptions: RecipientOption[] = uniqBy(
    [
      ...userMentions.map((r) => ({ ...r, is_mentioned: true })),
      ...groupMentions.map((r) => ({ ...r, is_mentioned: true })),
      ...to.map((r) => ({ ...r, is_mentioned: false, priority: 300 })),
    ],
    (r) => r.id,
  );

  return toRecipientOptions.map((recipient): DraftRecipientDoc => {
    switch (recipient.type) {
      case "user": {
        return pickBy(
          {
            id: generateRecordId("draft_user_recipient_doc", {
              type: "USER",
              user_id: recipient.id,
            }),
            type: "USER",
            user_id: recipient.id,
            priority: recipient.priority,
            respond_by: recipient.dueDate,
            request_complete: recipient.requestComplete,
            is_implicit: false,
            is_mentioned: recipient.is_mentioned,
          } satisfies DraftUserRecipientDoc,
          isDefined,
        ) as DraftUserRecipientDoc;
      }
      case "group": {
        return pickBy(
          {
            id: generateRecordId("draft_group_recipient_doc", {
              type: "GROUP",
              group_id: recipient.id,
            }),
            type: "GROUP",
            group_id: recipient.id,
            priority: recipient.priority,
            respond_by: recipient.dueDate,
            request_complete: recipient.requestComplete,
            is_implicit: false,
            is_mentioned: recipient.is_mentioned,
          } satisfies DraftGroupRecipientDoc,
          isDefined,
        ) as DraftGroupRecipientDoc;
      }
      case "email": {
        throw new InvalidActionStateError("[getToRecipients] Email drafts are not yet supported.", {
          errorData: { props },
        });
      }
      default: {
        throw new UnreachableCaseError(recipient.type);
      }
    }
  });
}

export const actions = {
  createNewThreadDraft,
  createReplyDraft,
  setDraft,
  sendDraft,
  deleteDraft,
  unDeleteDraft,
};
